import React, { useEffect, useMemo, useState, useRef, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Plus, Minus, Check, X, LogOut, AlertCircle, Search } from 'lucide-react';
import { useTenant } from '../../lib/tenant-context';
import { useTableManagement } from '../../lib/table-hooks';
import { useMenuCategories, useMenuItems } from '../../lib/menu-hooks';
import { TableWithOrder, MenuItem, CartItemOption } from '../../lib/types';
import { createNatuCafeAdditionPrintJob, createRanchoAdditionPrintJob } from '../../lib/print-additions';
import { formatCurrency, cn, aggregateQuantityOptions } from '../../lib/utils';
import { ProductDetailModal } from '../../components/restaurant/ProductDetailModal';
import { Button } from '../../components/ui/Button';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
import { ArmaTuPlatoPanel } from '../../components/restaurant/ArmaTuPlatoPanel';
import { EmiliusArmaTuPizzaModal } from '../../components/restaurant/EmiliusArmaTuPizzaModal';
// ─── MS Express external delivery catalog row (flexible shape) ──
interface MsExpressZoneRow {
  id: string | null;
  zone: string;
  subzone: string;
  fee: number;
}
function resolveMsExpressRow(r: any): MsExpressZoneRow | null {
  const zone = String(r?.zone ?? r?.zone_name ?? r?.provincia ?? '').trim();
  const subzone = String(r?.subzone ?? r?.subzone_name ?? r?.localidad ?? r?.locality ?? '').trim();
  const feeRaw = r?.delivery_fee ?? r?.fee ?? r?.price ?? r?.cost ?? r?.precio ?? 0;
  const fee = Number(feeRaw) || 0;
  if (!zone || !subzone) return null;
  return {
    id: r?.id != null ? String(r.id) : null,
    zone,
    subzone,
    fee
  };
}
interface WaiterCartItem {
  id: string;
  menuItem: MenuItem;
  quantity: number;
  options: CartItemOption[];
  notes: string;
}
// La Cabaña Parrillera (restaurant_id 24) — Tomar Orden supports
// three modes: Mesa (default, like other restaurants), Para llevar,
// and Express (con datos del cliente, por llamada telefónica).
type WaiterOrderMode = 'mesa' | 'pickup' | 'delivery';
interface WaiterCustomerInfo {
  name: string;
  phone: string;
  address: string;
  maps_url: string;
  notes: string;
  // Cabaña Parrillera — optional express fee (monto del express).
  // Stored as a string while editing so the input stays controllable;
  // converted to a number before submitting / totalizing.
  express_fee: string;
}
const EMPTY_CUSTOMER_INFO: WaiterCustomerInfo = {
  name: '',
  phone: '',
  address: '',
  maps_url: '',
  notes: '',
  express_fee: ''
};
const STORAGE_KEY_PREFIX = 'noiza_waiter_order_';
function getStorageKey(restaurantId: string, mode: WaiterOrderMode, tableId?: string | null) {
  if (mode === 'mesa' && tableId) {
    return `${STORAGE_KEY_PREFIX}${restaurantId}_${tableId}`;
  }
  return `${STORAGE_KEY_PREFIX}${restaurantId}_${mode}`;
}
function loadPersistedState(restaurantId: string | undefined, mode: WaiterOrderMode, tableId?: string | null) {
  if (!restaurantId || typeof window === 'undefined') return null;
  try {
    const key = getStorageKey(restaurantId, mode, tableId);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as {
      cart: WaiterCartItem[];
      customerInfo?: WaiterCustomerInfo;
    };
  } catch {
    return null;
  }
}
export function WaiterPOSPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    restaurant,
    role,
    signOut
  } = useTenant();
  const restaurantId = restaurant?.restaurant_id;
  const {
    tables,
    loading: tablesLoading
  } = useTableManagement(restaurantId);
  const {
    categories
  } = useMenuCategories(restaurantId);
  const {
    items: rawItems
  } = useMenuItems(restaurantId);
  // Filtrar productos de Estantería / POS retail (product_type='retail'
  // o internal_only=true). Estos productos se venden SOLO desde el
  // módulo Estantería (POS/Caja) y NUNCA deben aparecer en el flujo
  // de Tomar Orden de mesero (mesa / para llevar / express).
  // Defensivo: tolera columnas faltantes en DBs legacy.
  const items = useMemo(() => (rawItems || []).filter((it: any) => {
    if (it?.product_type === 'retail') return false;
    if (it?.internal_only === true) return false;
    if (it?.show_in_digital_menu === false) return false;
    return true;
  }), [rawItems]);
  // Orden de las mesas en la grilla. La fuente (`tables`) puede venir
  // ordenada como texto ("1","10","11",…,"2"), lo que en el celular se ve
  // como 1, 10, 11, 12… 2 — desordenado. Acá ordenamos numéricamente por
  // `table_number` cuando es numérico; si no lo es (ej. "Barra 5") caemos
  // a una comparación natural por locale. Es SOLO orden de presentación:
  // no toca datos ni el flujo de selección. Aplica a todos los
  // restaurantes por igual.
  const sortedTables = useMemo(() => {
    const arr = [...(tables || [])];
    arr.sort((a, b) => {
      const na = Number(a.table_number);
      const nb = Number(b.table_number);
      const aNum = !Number.isNaN(na);
      const bNum = !Number.isNaN(nb);
      if (aNum && bNum) return na - nb;
      if (aNum) return -1;
      if (bNum) return 1;
      return String(a.table_number).localeCompare(String(b.table_number), 'es', {
        numeric: true
      });
    });
    return arr;
  }, [tables]);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedTable, setSelectedTable] = useState<TableWithOrder | null>(null);
  // ─── orderMode initializer with self-healing ──────────────────────
  const [orderMode, setOrderMode] = useState<WaiterOrderMode>('mesa');
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState<WaiterCartItem[]>([]);
  const [customerInfo, setCustomerInfo] = useState<WaiterCustomerInfo>(EMPTY_CUSTOMER_INFO);
  const [showEmiliusArmaTuPizzaModal, setShowEmiliusArmaTuPizzaModal] = useState(false);
  // Load persisted state when table or mode changes
  useEffect(() => {
    if (!restaurantId) return;
    const effectiveMode = selectedTable ? 'mesa' : orderMode;
    const persisted = loadPersistedState(restaurantId, effectiveMode, selectedTable?.id);
    if (persisted) {
      setCart(persisted.cart || []);
      if (persisted.customerInfo) {
        setCustomerInfo(persisted.customerInfo);
      }
    } else {
      setCart([]);
      setCustomerInfo(EMPTY_CUSTOMER_INFO);
    }
  }, [restaurantId, selectedTable?.id, orderMode]);
  // ─── Arma tu plato (Rancho San Francisco, id 28) ──────────────────
  // Tab sintético al final de las categorías. Permite al mesero
  // escribir líneas libres de texto (ej. "Arroz", "4 chicharrones",
  // "Ensalada", "Papas") que se agregan al pedido como ítems
  // personalizados sin menu_item_id. La comanda en cocina los muestra
  // como cualquier otra línea — útil para platos armados a pedido.
  //
  // Implementación: cada línea es un WaiterCartItem con un MenuItem
  // sintético (id con prefijo `__custom__`, price 0). En el submit
  // (tanto edge function como merge directo) detectamos el prefijo y
  // mandamos `menu_item_id: null` — create-order.ts (línea 330) acepta
  // null para este campo. Scope: SOLO Rancho. Ningún otro restaurante
  // ve este tab.
  const [armaLines, setArmaLines] = useState<string[]>(['', '', '', '', '', '']);
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Anti-doble-click ref. Espejo síncrono de isSubmitting que no depende
  // del ciclo de render de React — el setState de isSubmitting no se
  // refleja en la siguiente ejecución del handler si el segundo click
  // entra antes del próximo render. submittingRef cierra esa ventana.
  // Mismo patrón que CheckoutPage.handleSubmit (lib pattern).
  const submittingRef = useRef(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // ─── Cabaña Parrillera — multi-mode support ──────────────────
  // Only enabled for restaurant_id === '24' / slug 'la-cabana-parrillera'.
  // Every other restaurant keeps the table-only flow it always had.
  const isCabanaParrillera = String(restaurant?.id) === '24' || String(restaurantId) === '24' || restaurant?.slug === 'la-cabana-parrillera' || restaurant?.slug === 'cabana-parrillera';
  // Rancho San Francisco (id 28) — después de enviar la orden el dueño
  // quiere volver directo a la pantalla de Mesas (su "home"), no al
  // step 1 / selector de estación. Sólo aplica a este restaurante.
  const isRanchoSanFrancisco = String(restaurant?.id) === '28' || String(restaurantId) === '28' || restaurant?.slug === 'rancho-san-francisco';
  // NatuCafé Coffee Shop (id 19) — mismo patrón post-orden que Rancho:
  // volver a Mesas después de enviar para que el flujo "mesa → orden
  // → mesa" sea instantáneo durante el rush. NatuCafé también gana un
  // toque de branding (logo en el top bar). Ningún query se comparte
  // con otros restaurantes — solo banderas de UI scopeadas a id 19.
  const isNatuCafe = String(restaurant?.id) === '19' || String(restaurantId) === '19' || restaurant?.slug === 'natucafe-coffee-shop';
  // Emilius Pizza (id 27) — mismo flujo que NatuCafé en Tomar Orden:
  // vuelve a Mesas tras enviar y soporta Mesa / Para llevar / Express.
  // Emilius NO comparte el hideo de categorías ni el orden custom de
  // NatuCafé — usa sus categorías tal cual. NatuCafé NO se ve afectado.
  const isEmilius = String(restaurant?.id) === '27' || String(restaurantId) === '27' || restaurant?.slug === 'emilius-pizza';
  const returnsToTablesAfterSubmit = isRanchoSanFrancisco || isNatuCafe || isEmilius;
  // Multi-mode (Mesa / Para llevar / Express) — habilitado para Cabaña
  // Parrillera, NatuCafé, Rancho San Francisco y Emilius. Cualquier otra
  // ubicación sigue con el flujo clásico de solo-mesa.
  const supportsMultiMode = isCabanaParrillera || isNatuCafe || isRanchoSanFrancisco || isEmilius;
  // ─── MS Express external delivery (NatuCafé) ──────────────────────────
  const [msZones, setMsZones] = useState<MsExpressZoneRow[]>([]);
  const [msZonesLoading, setMsZonesLoading] = useState(false);
  const [selectedMsZone, setSelectedMsZone] = useState<string>('');
  const [selectedMsSubzone, setSelectedMsSubzone] = useState<string>('');
  const [selectedMsFee, setSelectedMsFee] = useState<number>(0);
  const [selectedMsCatalogId, setSelectedMsCatalogId] = useState<string | null>(null);
  // Load zones only when needed
  useEffect(() => {
    if (!isNatuCafe || orderMode !== 'delivery') return;
    let cancelled = false;
    const loadZones = async () => {
      setMsZonesLoading(true);
      try {
        const {
          data,
          error
        } = await supabase.from('delivery_zones_catalog').select('id, zone, subzone, price').eq('provider_name', 'MS Express').is('restaurant_id', null).eq('is_active', true).order('zone', {
          ascending: true
        }).order('price', {
          ascending: true
        }).order('subzone', {
          ascending: true
        });
        if (error) {
          console.error('[MS_EXPRESS] delivery_zones_catalog error:', error);
          if (!cancelled) setMsZones([]);
          return;
        }
        const rows = (data || []).map(resolveMsExpressRow).filter((r): r is MsExpressZoneRow => r !== null);
        if (!cancelled) setMsZones(rows);
      } catch (err) {
        console.error('[MS_EXPRESS] load zones exception:', err);
        if (!cancelled) setMsZones([]);
      } finally {
        if (!cancelled) setMsZonesLoading(false);
      }
    };
    loadZones();
    return () => {
      cancelled = true;
    };
  }, [isNatuCafe, orderMode]);
  // Reset MS Express selection when leaving delivery
  useEffect(() => {
    if (orderMode !== 'delivery') {
      setSelectedMsZone('');
      setSelectedMsSubzone('');
      setSelectedMsFee(0);
      setSelectedMsCatalogId(null);
    }
  }, [orderMode]);
  const msZoneNames = useMemo(() => Array.from(new Set(msZones.map(z => z.zone))).sort((a, b) => a.localeCompare(b, 'es')), [msZones]);
  const msSubzonesForZone = useMemo(() => msZones.filter(z => z.zone === selectedMsZone).sort((a, b) => {
    if (a.fee !== b.fee) return a.fee - b.fee;
    return a.subzone.localeCompare(b.subzone, 'es');
  }), [msZones, selectedMsZone]);
  // Persist whenever cart or customer info changes
  useEffect(() => {
    if (!restaurantId || typeof window === 'undefined') return;
    const effectiveMode = selectedTable ? 'mesa' : orderMode;
    const key = getStorageKey(restaurantId, effectiveMode, selectedTable?.id);
    const isEmptyCustomer = !customerInfo.name && !customerInfo.phone && !customerInfo.address && !customerInfo.notes;
    if (cart.length === 0 && isEmptyCustomer) {
      localStorage.removeItem(key);
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify({
        cart,
        customerInfo
      }));
    } catch {
      // Storage full or unavailable — silently ignore
    }
  }, [restaurantId, selectedTable, cart, orderMode, customerInfo]);
  // Filter active categories and items.
  //
  // NatuCafé (id 19) — además del filtro estándar `is_active`, escondemos
  // los "parents" que en esta pantalla quedan vacíos o son redundantes:
  // "Bebidas y Repostería" y "Comida". Sus items reales viven en las
  // sub-categorías (Bebidas calientes, Bebidas frías, Repostería,
  // Sánguches, Platos fuertes, etc.) que sí se muestran. Esta es una
  // capa visual SOLO en "Tomar Orden" — no se borra nada de la DB, y el
  // menú público / KDS siguen viendo todas las categorías como siempre.
  const hiddenWaiterCategoryNames = useMemo(() => {
    if (!isNatuCafe) return new Set<string>();
    return new Set(['bebidas y reposteria', 'bebidas y repostería', 'comida'].map(s => s.toLowerCase()));
  }, [isNatuCafe]);
  // NatuCafé (id 19) — el menú público agrupa las subcategorías en dos
  // "parents": 'bebidas' (Bebidas y Repostería) y 'comida'. Dentro de cada
  // grupo el orden es por DB sort_order. En "Tomar Orden" debemos verlas
  // EXACTAMENTE en el mismo orden, no mezcladas alfabéticamente ni por
  // sort_order global. Esta lógica es 1:1 con RestaurantPage > getNatuCafeParentKey.
  // Scope: SOLO restaurant id 19 / slug natucafe-coffee-shop. Ningún otro
  // restaurante toca esta rama.
  const getNatuCafeParent = (name: string): 'bebidas' | 'comida' => {
    const n = (name || '').toLowerCase().trim();
    const bebidasSubs = ['bebidas calientes', 'bebidas frías', 'bebidas frias', 'té y más', 'te y mas', 'aguadulce', 'batidos', 'batidos en agua', 'batidos con leche', 'postres', 'repostería', 'reposteria'];
    if (bebidasSubs.some(sub => n.includes(sub) || sub.includes(n))) {
      return 'bebidas';
    }
    return 'comida';
  };
  const activeCategories = useMemo(() => {
    const filtered = categories.filter(c => c.is_active).filter(c => !hiddenWaiterCategoryNames.has((c.name || '').trim().toLowerCase()));
    if (isNatuCafe) {
      const bebidas = filtered.filter(c => getNatuCafeParent(c.name || '') === 'bebidas').sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      const comida = filtered.filter(c => getNatuCafeParent(c.name || '') === 'comida').sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      return [...bebidas, ...comida];
    }
    return filtered.sort((a, b) => a.sort_order - b.sort_order);
  }, [categories, hiddenWaiterCategoryNames, isNatuCafe]);
  // Append sintético "Arma tu plato" al final — Rancho (siempre) +
  // NatuCafé (sólo en modo Mesa). Scope quirúrgico: el tab NO aparece en
  // marketplace, QR auto-atención, ni cuando un restaurante usa flujos
  // pickup/delivery. La infra para items con menu_item_id=NULL ya es
  // restaurant-agnostic (create-order.ts, PayTableModal, bridge,
  // triggers de print/station_code), así que NatuCafé reusa el mismo
  // pipeline sin cambios adicionales.
  const ARMA_TU_PLATO_ID = '__arma_tu_plato__';
  const armaTuPlatoEnabled = isRanchoSanFrancisco || isNatuCafe && orderMode === 'mesa';
  const displayCategories = useMemo(() => {
    if (!armaTuPlatoEnabled) return activeCategories;
    return [...activeCategories, {
      id: ARMA_TU_PLATO_ID,
      name: 'Arma tu plato',
      is_active: true,
      sort_order: 9999
    } as any];
  }, [activeCategories, armaTuPlatoEnabled]);
  const activeItems = useMemo(() => items.filter(i => i.is_active).sort((a, b) => a.sort_order - b.sort_order), [items]);
  // Set initial category
  useEffect(() => {
    if (activeCategories.length > 0 && !activeCategoryId) {
      setActiveCategoryId(activeCategories[0].id);
    }
  }, [activeCategories, activeCategoryId]);
  // ─── Defensa: si el tab Arma tu plato se desactiva (ej. NatuCafé cambia
  // de Mesa → Para llevar mientras estaba abierto el tab), reseteamos
  // activeCategoryId a la primera categoría real. Sin esto el grid queda
  // mostrando "No hay productos" — funciona pero confunde al cajero.
  useEffect(() => {
    if (activeCategoryId === ARMA_TU_PLATO_ID && !armaTuPlatoEnabled && activeCategories.length > 0) {
      setActiveCategoryId(activeCategories[0].id);
    }
  }, [armaTuPlatoEnabled, activeCategoryId, activeCategories]);
  const cartTotal = cart.reduce((sum, item) => {
    const optionsTotal = item.options.reduce((optSum, opt) => optSum + (Number(opt.price_delta) || 0), 0);
    return sum + (Number(item.menuItem.price) + optionsTotal) * item.quantity;
  }, 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  // Cabaña Parrillera — Express fee adds to total only for delivery mode.
  // NatuCafé — uses selectedMsFee instead of manual input.
  const expressFeeAmount = supportsMultiMode && orderMode === 'delivery' ? isNatuCafe ? selectedMsFee : Math.max(0, Number(customerInfo.express_fee) || 0) : 0;
  const orderTotal = cartTotal + expressFeeAmount;
  const handleTableSelect = (table: TableWithOrder) => {
    setSelectedTable(table);
    setStep(2);
  };
  // ─── Auto-select table from `?mesa=<table_number>` URL param ──────
  // Used by Rancho's Mesas grid (and any other restaurant in the future)
  // to deep-link straight into the "Tomar Orden" flow for a specific
  // mesa. Runs ONCE when tables are loaded and the URL param is present.
  const autoSelectedRef = useRef(false);
  // ─── Auto-select orderMode from `?mode=pickup|delivery|mesa` ─────
  // Used by NatuCafé's TableManagementPage tipo-de-orden cards para
  // deep-link al flujo correcto. Solo aplica si el restaurante
  // soporta multi-mode; el resto ignora el param.
  const modeSelectedRef = useRef(false);
  useEffect(() => {
    if (modeSelectedRef.current) return;
    if (!supportsMultiMode) return;
    const modeParam = searchParams.get('mode');
    if (modeParam === 'pickup' || modeParam === 'delivery') {
      modeSelectedRef.current = true;
      setOrderMode(modeParam as WaiterOrderMode);
      setSelectedTable(null);
      // CRÍTICO: para "Para llevar" (pickup) y "Express" (delivery) el
      // paso 1 es el formulario de DATOS DEL CLIENTE (nombre, teléfono,
      // dirección). NO saltar a step 2 (menú) — eso se salta la captura
      // de datos. Aterrizamos en step 1 para que el cajero llene los
      // datos primero y de ahí avance al menú con "Continuar".
      // (Las mesas SÍ van directo a step 2 porque no tienen este
      //  formulario — ver el deep-link ?mesa=xxx más abajo.)
      setStep(1);
      // Remove from URL so it doesn't stick if they go back
      const next = new URLSearchParams(searchParams);
      next.delete('mode');
      setSearchParams(next, {
        replace: true
      });
    }
  }, [supportsMultiMode, searchParams, setSearchParams]);
  // If URL has ?mesa=xxx, auto-select it and go to step 2
  useEffect(() => {
    if (tablesLoading || tables.length === 0) return;
    const mesaParam = searchParams.get('mesa');
    if (mesaParam) {
      const found = tables.find(t => t.id === mesaParam || String(t.table_number) === mesaParam || t.table_name?.toLowerCase() === mesaParam.toLowerCase());
      if (found) {
        setSelectedTable(found);
        setOrderMode('mesa');
        setStep(2);
      }
      // Remove from URL so it doesn't stick if they go back
      const next = new URLSearchParams(searchParams);
      next.delete('mesa');
      setSearchParams(next, {
        replace: true
      });
    }
  }, [tables, tablesLoading, searchParams, setSearchParams]);
  const handleAddToCart = (menuItem: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    setCart(prev => [...prev, {
      id: crypto.randomUUID(),
      menuItem,
      quantity,
      options,
      notes
    }]);
  };
  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? {
          ...item,
          quantity: newQty
        } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };
  const removeItem = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };
  const handleSubmitOrder = async () => {
    if (!restaurant || cart.length === 0) return;
    // ─── Effective mode (anti-orphan guard, pre-validation) ─────────
    // Si hay una mesa seleccionada, la orden es SIEMPRE mesa, sin
    // importar lo que diga orderMode. Esto evita que un estado
    // inconsistente (orderMode='delivery' con selectedTable set,
    // típicamente por localStorage stale) bloquee al usuario con
    // "La dirección es requerida" o cree una orden delivery sin
    // table_id. Las validaciones de abajo usan effectiveMode, el
    // payload de submit también — todo alineado en un solo lugar.
    const effectiveMode: WaiterOrderMode = selectedTable ? 'mesa' : orderMode;
    // Mesa requires a real table; pickup/delivery don't
    if (effectiveMode === 'mesa' && !selectedTable) return;
    if (effectiveMode === 'delivery' && !customerInfo.address.trim()) {
      setSubmitError('La dirección es requerida para pedidos express.');
      return;
    }
    if (isNatuCafe && effectiveMode === 'delivery' && (!selectedMsZone || !selectedMsSubzone)) {
      setSubmitError('Debe seleccionar la zona y localidad de entrega.');
      return;
    }
    if ((effectiveMode === 'pickup' || effectiveMode === 'delivery') && !customerInfo.name.trim()) {
      setSubmitError('El nombre del cliente es requerido.');
      return;
    }
    // ─── Anti-doble-click hard gate ─────────────────────────────────
    // Si una llamada anterior todavía está en vuelo, descartamos esta
    // SILENCIOSAMENTE (sin tocar estados ni mostrar errores). Esto
    // bloquea el caso de dos clicks rapidísimos antes de que React
    // refleje setIsSubmitting(true) en el próximo render. Sin este
    // gate, el segundo click podía generar un order_intent_id distinto
    // y crear DOS órdenes en paralelo (el fingerprint MD5 atrapa la
    // mayoría, pero esto cierra la ventana en origen).
    //
    // Colocado DESPUÉS de las validaciones (no antes) para que validación
    // fallida no deje el ref bloqueado. Cuando una validación retorna
    // temprano, el ref nunca se setea — el siguiente click puede entrar
    // normal una vez que el usuario corrija el form.
    if (submittingRef.current) {
      console.warn('[WAITER POS] submit ignored — already in flight');
      return;
    }
    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // ─── La Cabaña Parrillera — merge into existing open table order ───
      // When a table is already occupied with an open order, adding more
      // products from "Tomar Orden" must APPEND to that order, not create
      // a second one. Mirrors CabanaAddItemsModal logic so the KDS sees
      // the new items inside the same comanda.
      const existingOrderId = supportsMultiMode && effectiveMode === 'mesa' && selectedTable?.current_order?.id ? selectedTable.current_order.id : null;
      if (existingOrderId) {
        try {
          // VALIDATE order belongs to table before merging
          const {
            data: validateOrder
          } = await supabase.from('orders').select('table_id, restaurant_id').eq('id', existingOrderId).maybeSingle();
          if (!validateOrder || String(validateOrder.table_id) !== String(selectedTable?.id) || String(validateOrder.restaurant_id) !== String(restaurantId)) {
            throw new Error('Esta orden no corresponde a esta mesa. Recargá la mesa.');
          }
          console.log('[NOIZA][SEND_TO_KITCHEN]', {
            restaurantId,
            tableId: selectedTable?.id,
            orderId: existingOrderId,
            orderNumber: selectedTable?.current_order?.order_number
          });
          // 1) Insert each new order_item (same shape as create-order.ts)
          for (const ci of cart) {
            const optTotal = (ci.options || []).reduce((s, o: any) => s + (Number(o.price_delta) || 0), 0);
            const unitPrice = Number(ci.menuItem.price) || 0;
            const lineTotal = (unitPrice + optTotal) * ci.quantity;
            const isCustomArmaItem = typeof ci.menuItem.id === 'string' && (ci.menuItem.id as string).startsWith('__custom__');
            const {
              data: inserted,
              error: itemErr
            } = await supabase.from('order_items').insert({
              order_id: existingOrderId,
              restaurant_id: Number(restaurantId),
              menu_item_id: isCustomArmaItem ? null : ci.menuItem.id,
              item_name_snapshot: ci.menuItem.name,
              unit_price_snapshot: unitPrice,
              quantity: ci.quantity,
              notes: ci.notes || null,
              line_total: lineTotal,
              status: 'pending'
            }).select('id').single();
            if (itemErr) throw itemErr;
            if ((ci.options || []).length > 0 && inserted) {
              const optsPayload = ci.options.map((o: any) => ({
                order_item_id: inserted.id,
                restaurant_id: Number(restaurantId),
                option_group: o.option_group || '',
                option_name: o.option_name || '',
                price_delta: Number(o.price_delta) || 0
              }));
              const {
                error: optErr
              } = await supabase.from('order_item_options').insert(optsPayload);
              if (optErr) {
                console.warn('[WAITER POS][cabana-merge] options insert non-blocking:', optErr);
              }
            }
          }
          // 2) Recompute order subtotal/total from surviving items
          const {
            data: allItems,
            error: fetchErr
          } = await supabase.from('order_items').select('line_total, status').eq('order_id', existingOrderId);
          if (fetchErr) throw fetchErr;
          const newSubtotal = (allItems || []).filter((it: any) => it.status !== 'cancelled').reduce((s: number, it: any) => s + (Number(it.line_total) || 0), 0);
          const {
            data: orderRow
          } = await supabase.from('orders').select('delivery_fee, status').eq('id', existingOrderId).maybeSingle();
          const deliveryFee = Number(orderRow?.delivery_fee) || 0;
          const newTotal = newSubtotal + deliveryFee;
          const updatePayload: Record<string, any> = {
            subtotal: newSubtotal,
            total: newTotal,
            updated_at: new Date().toISOString()
          };
          // If the order was already marked 'ready', bump it back to
          // 'preparing' so KDS shows it again with the new pending items.
          if (orderRow?.status === 'ready') {
            updatePayload.status = 'preparing';
          }
          const {
            error: updErr
          } = await supabase.from('orders').update(updatePayload).eq('id', existingOrderId);
          if (updErr) throw updErr;
          // ─── NatuCafé (id 19) — adición a mesa abierta ─────────────
          // Mesa ya tenía comanda inicial impresa; el trigger de DB no
          // dispara una segunda vez. Acá enviamos a cocina/barra SOLO
          // los items recién agregados, con la mesa correcta y un
          // bloque 'NOTA: ADICIÓN'. No-bloqueante.
          if (Number(restaurantId) === 19 || String(restaurantId) === '19') {
            try {
              const addedItemsForPrint = cart.map(ci => {
                const isCustomArmaItem = typeof ci.menuItem.id === 'string' && (ci.menuItem.id as string).startsWith('__custom__');
                const optTotal = (ci.options || []).reduce((s: number, o: any) => s + (Number(o.price_delta) || 0), 0);
                const unitPrice = isCustomArmaItem ? 0 : Number(ci.menuItem.price) || 0;
                const lineTotal = isCustomArmaItem ? 0 : (unitPrice + optTotal) * ci.quantity;
                return {
                  menu_item_id: isCustomArmaItem ? null : ci.menuItem.id as string,
                  item_name_snapshot: ci.menuItem.name,
                  unit_price_snapshot: unitPrice,
                  quantity: ci.quantity,
                  notes: ci.notes || null,
                  line_total: lineTotal,
                  station_code: (ci.menuItem as any).station_code ?? null,
                  selected_options: (ci.options || []).map((o: any) => ({
                    option_group: o.option_group || '',
                    option_name: o.option_name || '',
                    price_delta: Number(o.price_delta) || 0
                  }))
                };
              });
              const result = await createNatuCafeAdditionPrintJob({
                restaurantId,
                orderId: existingOrderId,
                addedItems: addedItemsForPrint
              });
              if (!result.ok) {
                console.warn('[NATUCAFE_ADD_PRINT][WaiterPOS] skipped/failed:', result.reason);
              } else {
                console.log('[NATUCAFE_ADD_PRINT][WaiterPOS] ✅ print job created:', result.jobId);
              }
            } catch (printErr) {
              console.warn('[NATUCAFE_ADD_PRINT][WaiterPOS] exception:', printErr);
            }
          }
          // ─── Rancho San Francisco (id 28) — adición a mesa abierta ──
          // Igual que NatuCafé: la mesa ya tenía comanda inicial. El
          // bridge de Rancho imprime cocina SÓLO desde payload.items, así
          // que mandamos un print job de adición con ÚNICAMENTE los ítems
          // recién agregados (con sus opciones y notas). Esto evita que se
          // reimprima la orden completa y garantiza que el detalle ("de
          // qué es el natural", etc.) salga siempre. No-bloqueante.
          if (Number(restaurantId) === 28 || String(restaurantId) === '28') {
            try {
              const addedItemsForPrint = cart.map(ci => {
                const isCustomArmaItem = typeof ci.menuItem.id === 'string' && (ci.menuItem.id as string).startsWith('__custom__');
                const optTotal = (ci.options || []).reduce((s: number, o: any) => s + (Number(o.price_delta) || 0), 0);
                const unitPrice = isCustomArmaItem ? 0 : Number(ci.menuItem.price) || 0;
                const lineTotal = isCustomArmaItem ? 0 : (unitPrice + optTotal) * ci.quantity;
                return {
                  menu_item_id: isCustomArmaItem ? null : ci.menuItem.id as string,
                  item_name_snapshot: ci.menuItem.name,
                  unit_price_snapshot: unitPrice,
                  quantity: ci.quantity,
                  notes: ci.notes || null,
                  line_total: lineTotal,
                  station_code: (ci.menuItem as any).station_code ?? null,
                  selected_options: (ci.options || []).map((o: any) => ({
                    option_group: o.option_group || '',
                    option_name: o.option_name || '',
                    price_delta: Number(o.price_delta) || 0
                  }))
                };
              });
              const result = await createRanchoAdditionPrintJob({
                restaurantId,
                orderId: existingOrderId,
                addedItems: addedItemsForPrint
              });
              if (!result.ok) {
                console.warn('[RANCHO_ADD_PRINT][WaiterPOS] skipped/failed:', result.reason);
              } else {
                console.log('[RANCHO_ADD_PRINT][WaiterPOS] ✅ print job created:', result.jobId);
              }
            } catch (printErr) {
              console.warn('[RANCHO_ADD_PRINT][WaiterPOS] exception:', printErr);
            }
          }
        } catch (mergeErr: any) {
          console.error('[WAITER POS][cabana-merge] error:', mergeErr);
          throw new Error(mergeErr?.message || 'No se pudo adicionar a la mesa');
        }
        // Success — same reset flow as the create path below.
        setCart([]);
        setSelectedTable(null);
        setOrderMode('mesa');
        setCustomerInfo(EMPTY_CUSTOMER_INFO);
        setStep(1);
        if (restaurantId && typeof window !== 'undefined') {
          const key = getStorageKey(restaurantId, effectiveMode, selectedTable?.id);
          localStorage.removeItem(key);
        }
        return;
      }
      console.log('[NOIZA][SEND_TO_KITCHEN]', {
        restaurantId,
        tableId: selectedTable?.id,
        orderId: null,
        orderNumber: null
      });
      const orderIntentId = crypto.randomUUID();
      let payload: Record<string, any>;
      // 🔧 STATION ROUTING: cada item del carrito carga consigo el
      // `station_code` configurado en `menu_items` (Editar Producto →
      // Estación de preparación). Lo enviamos EXPLÍCITAMENTE en el
      // payload para que el edge function lo persista en `order_items`
      // y el KDS pueda filtrar por estación. Esto reemplaza la dependencia
      // del trigger de DB (que en algunos restaurantes está como no-op).
      // Cobertura segura: Cabaña / Rancho / NatuCafé / etc.
      //
      // ─── Anti-orphan guard ───────────────────────────────────────
      // BLINDAJE: si effectiveMode === 'mesa' (computado arriba de la
      // misma forma para validación), la orden SIEMPRE se crea como
      // dine_in con table_id. Esto cierra estructuralmente la puerta
      // a las órdenes huérfanas (source=waiter + table_id=null) que el
      // bug viejo de localStorage podía producir.
      if (effectiveMode === 'mesa') {
        payload = {
          restaurant_id: restaurantId,
          order_intent_id: orderIntentId,
          customer_name: `Mesa ${selectedTable!.table_number}`,
          delivery_type: 'dine_in',
          table_id: selectedTable!.id,
          source: 'waiter',
          subtotal: cartTotal,
          total: cartTotal,
          items: cart.map(item => {
            // Arma tu plato — ítems sintéticos sin menu_item_id real.
            const isCustomArmaItem = typeof item.menuItem.id === 'string' && (item.menuItem.id as string).startsWith('__custom__');
            return {
              menu_item_id: isCustomArmaItem ? null : item.menuItem.id,
              item_name_snapshot: item.menuItem.name,
              unit_price_snapshot: item.menuItem.price,
              quantity: item.quantity,
              notes: item.notes,
              selected_options: item.options,
              station_code: (item.menuItem as any).station_code ?? null
            };
          })
        };
      } else {
        const isDelivery = orderMode === 'delivery';
        payload = {
          restaurant_id: restaurantId,
          order_intent_id: orderIntentId,
          customer_name: customerInfo.name.trim(),
          customer_phone: customerInfo.phone.trim() || null,
          delivery_type: isDelivery ? 'delivery' : 'pickup',
          table_id: null,
          delivery_address: isDelivery ? customerInfo.address.trim() || null : null,
          delivery_zone: isDelivery && isNatuCafe ? selectedMsZone || null : null,
          delivery_subzone: isDelivery && isNatuCafe ? selectedMsSubzone || null : null,
          delivery_zone_catalog_id: isDelivery && isNatuCafe ? selectedMsCatalogId || null : null,
          maps_url: isDelivery ? customerInfo.maps_url.trim() || null : null,
          notes: customerInfo.notes.trim() || null,
          payment_method: 'cash',
          source: 'waiter',
          subtotal: cartTotal,
          delivery_fee: isDelivery ? expressFeeAmount : 0,
          total: cartTotal + (isDelivery ? expressFeeAmount : 0),
          items: cart.map(item => {
            const isCustomArmaItem = typeof item.menuItem.id === 'string' && (item.menuItem.id as string).startsWith('__custom__');
            return {
              menu_item_id: isCustomArmaItem ? null : item.menuItem.id,
              item_name_snapshot: item.menuItem.name,
              unit_price_snapshot: item.menuItem.price,
              quantity: item.quantity,
              notes: item.notes,
              selected_options: item.options,
              station_code: (item.menuItem as any).station_code ?? null
            };
          })
        };
      }
      console.log('[WAITER POS] orderMode:', orderMode);
      console.log('[WAITER POS] Submitting payload:', payload);
      const response = await fetch(`${SUPABASE_URL}/functions/v1/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || 'Error al crear la orden');
      }
      let createdOrderId: string | number | null = result?.order?.id ?? null;
      let createdOrderUuid: string | null = result?.order?.tracking_token ?? null;
      let createdOrderNumber: string | number | null = result?.order?.order_number ?? null;
      if (!createdOrderId || !createdOrderUuid) {
        throw new Error('No se pudo obtener el ID de la orden creada. Por favor, recargá la página.');
      }
      console.log('[NOIZA][TABLE_ORDER_RESOLVED]', {
        restaurantId,
        tableId: selectedTable?.id,
        orderId: createdOrderId,
        orderNumber: createdOrderNumber
      });
      // VALIDATE order belongs to table before proceeding
      if (effectiveMode === 'mesa') {
        const {
          data: validateOrder
        } = await supabase.from('orders').select('table_id, restaurant_id').eq('id', createdOrderId).maybeSingle();
        if (!validateOrder || String(validateOrder.table_id) !== String(selectedTable?.id) || String(validateOrder.restaurant_id) !== String(restaurantId)) {
          throw new Error('Esta orden no corresponde a esta mesa. Recargá la mesa.');
        }
      }
      // ─── Rancho San Francisco (id 28) — Print job (with diagnostics) ─
      // Logs DE DEBUG sin filtro de restaurante para diagnosticar por qué
      // no se está creando la fila en order_print_jobs en producción.
      // Solo el INSERT está gated a Rancho.
      console.log('[RANCHO_PRINT_TABLES] restaurantId:', restaurantId);
      console.log('[RANCHO_PRINT_TABLES] restaurant:', restaurant);
      console.log('[RANCHO_PRINT_TABLES] order response:', result);
      const isRancho28 = Number(restaurantId) === 28 || Number(restaurant?.id) === 28;
      if (isRancho28 && !result?.duplicate_prevented) {
        try {
          console.log('[RANCHO_PRINT_TABLES] resolved ids — numeric id:', createdOrderId, ' uuid (tracking_token):', createdOrderUuid);
          console.log('[RANCHO_PRINT_TABLES] Creating print job for order', createdOrderId);
          // SIN existence check bloqueante. Vamos directo al INSERT.
          // La dedupe se maneja con UNIQUE INDEX en Supabase si se
          // requiere (ver SQL al final del cambio).
          {
            // Construimos el payload SIN dependencia obligatoria de
            // fetches con joins. Si el fetch falla, igual insertamos
            // con la info que ya tenemos (cart, payload, response,
            // selectedTable, customerInfo).
            // 1) Fetch simple de la orden (SIN joins, .maybeSingle())
            let fullOrder: any = null;
            try {
              const {
                data: ord,
                error: ordErr
              } = await supabase.from('orders').select('*').eq('id', createdOrderId).maybeSingle();
              if (ordErr) {
                console.warn('[RANCHO_PRINT_TABLES] order fetch error (non-blocking)', ordErr);
              } else {
                fullOrder = ord;
              }
            } catch (e) {
              console.warn('[RANCHO_PRINT_TABLES] order fetch threw (non-blocking)', e);
            }
            // 2) Fetch simple de order_items (SIN joins). Si falla,
            //    fallback al cart actual (ya tenemos los productos).
            let orderItems: any[] = [];
            try {
              const {
                data: items,
                error: itemsErr
              } = await supabase.from('order_items').select('*').eq('order_id', createdOrderId);
              if (itemsErr) {
                console.warn('[RANCHO_PRINT_TABLES] items fetch error (non-blocking)', itemsErr);
              } else if (items && items.length > 0) {
                orderItems = items;
              }
            } catch (e) {
              console.warn('[RANCHO_PRINT_TABLES] items fetch threw (non-blocking)', e);
            }
            // 3) Construir el payload de items para el print job
            let itemsPayload: any[] = [];
            if (orderItems.length > 0) {
              // Si logramos leer de la DB, usamos eso (es la fuente de verdad)
              itemsPayload = orderItems.map(it => ({
                menu_item_id: it.menu_item_id,
                item_name_snapshot: it.item_name_snapshot,
                unit_price_snapshot: it.unit_price_snapshot,
                quantity: it.quantity,
                notes: it.notes,
                station_code: it.station_code,
                line_total: it.line_total,
                selected_options: [] // Omitimos opciones por simplicidad en el fallback
              }));
            } else {
              // Fallback al cart local si la DB falló
              itemsPayload = cart.map(ci => {
                const isCustomArmaItem = typeof ci.menuItem.id === 'string' && (ci.menuItem.id as string).startsWith('__custom__');
                const optTotal = (ci.options || []).reduce((s, o: any) => s + (Number(o.price_delta) || 0), 0);
                const unitPrice = isCustomArmaItem ? 0 : Number(ci.menuItem.price) || 0;
                const lineTotal = isCustomArmaItem ? 0 : (unitPrice + optTotal) * ci.quantity;
                return {
                  menu_item_id: isCustomArmaItem ? null : ci.menuItem.id as string,
                  item_name_snapshot: ci.menuItem.name,
                  unit_price_snapshot: unitPrice,
                  quantity: ci.quantity,
                  notes: ci.notes || null,
                  line_total: lineTotal,
                  station_code: (ci.menuItem as any).station_code ?? null,
                  selected_options: (ci.options || []).map((o: any) => ({
                    option_group: o.option_group || '',
                    option_name: o.option_name || '',
                    price_delta: Number(o.price_delta) || 0
                  }))
                };
              });
            }
            const printPayload = {
              trigger: 'rancho_new',
              order_id: createdOrderId,
              order_number: createdOrderNumber,
              tracking_token: createdOrderUuid,
              created_at: fullOrder?.created_at || new Date().toISOString(),
              customer_name: fullOrder?.customer_name || payload.customer_name,
              customer_phone: fullOrder?.customer_phone || payload.customer_phone,
              delivery_type: fullOrder?.delivery_type || payload.delivery_type,
              payment_method: fullOrder?.payment_method || payload.payment_method,
              table_id: fullOrder?.table_id || payload.table_id,
              notes: fullOrder?.notes || payload.notes,
              subtotal: fullOrder?.subtotal || payload.subtotal,
              delivery_fee: fullOrder?.delivery_fee || payload.delivery_fee,
              total: fullOrder?.total || payload.total,
              source: fullOrder?.source || payload.source,
              items: itemsPayload
            };
            console.log('[RANCHO_PRINT_TABLES] Inserting print job with payload:', printPayload);
            const {
              error: pjError
            } = await supabase.from('order_print_jobs').insert({
              restaurant_id: 28,
              order_id: createdOrderUuid,
              order_number: createdOrderNumber,
              status: 'pending',
              payload: printPayload
            });
            if (pjError) {
              console.error('[RANCHO_PRINT_TABLES] Failed to insert print job:', pjError);
            } else {
              console.log('[RANCHO_PRINT_TABLES] Print job inserted successfully');
            }
          }
        } catch (printErr) {
          console.error('[RANCHO_PRINT_TABLES] Unhandled exception creating print job:', printErr);
        }
      }
      // Success! Clear cart and go back to step 1
      setCart([]);
      setSelectedTable(null);
      setOrderMode('mesa');
      setCustomerInfo(EMPTY_CUSTOMER_INFO);
      setStep(1);
      if (restaurantId && typeof window !== 'undefined') {
        const key = getStorageKey(restaurantId, effectiveMode, selectedTable?.id);
        localStorage.removeItem(key);
      }
      // Si el restaurante tiene el flag para volver a mesas, navegamos
      // a la grilla de Mesas del MISMO restaurante DENTRO del admin.
      // ⚠️ FIX: antes navegaba a `/r/${slug}/tables` — un prefijo de
      // ruta pública que NO existe en el router, así que caía al
      // catch-all del marketplace y terminaba redirigiendo a "/"
      // (noizacr.com). La ruta correcta de la vista de mesas es
      // `/admin/:slug/tables` (ver App.tsx). Si el slug faltara por
      // alguna razón, nos quedamos en /admin/<slug>/waiter como
      // fallback seguro — nunca al marketplace.
      if (returnsToTablesAfterSubmit && restaurant?.slug) {
        navigate(`/admin/${restaurant.slug}/tables`);
      }
    } catch (err: any) {
      console.error('[WAITER POS] Submit error:', err);
      setSubmitError(err.message || 'Error de conexión');
    } finally {
      setIsSubmitting(false);
      // Liberar el gate síncrono. Siempre se ejecuta porque solo
      // entramos al try después de pasar el gate. Las validaciones
      // tempranas (que returnean antes del gate) ni siquiera setean
      // el ref, así que no necesitan liberarlo.
      submittingRef.current = false;
    }
  };
  if (!restaurant) return null;
  return <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-6504" data-mp="1etmg2lfh5dl6">
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-40 shadow-sm" data-id="element-6505" data-mp="1tia9al1ulb7y">
        <div className="flex items-center gap-3" data-id="element-6506" data-mp="1ombmv302y3a6">
          {isNatuCafe && restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-8 h-8 rounded-lg object-cover border border-gray-100 bg-white" onError={e => {
          ;
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }} data-id="element-6507" data-mp="10wqtrra7ppn6" /> : <div className="w-8 h-8 bg-brand-charcoal rounded-lg flex items-center justify-center text-white font-bold" data-id="element-6508" data-mp="2l9ydgkah2rfp">
              {restaurant.name.charAt(0)}
            </div>}
          <span className="font-bold text-gray-900 truncate max-w-[150px]" data-id="element-6509" data-mp="2orvxm940o6x3">
            {restaurant.name}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm font-medium text-gray-500" data-id="element-6510" data-mp="2ts9y963agel3">
          <span className={cn(step >= 1 ? 'text-brand-charcoal font-bold' : '')} data-id="element-6511" data-mp="2evaq9h4iyjq3">
            1{' '}
            {supportsMultiMode && orderMode === 'pickup' ? 'Llevar' : supportsMultiMode && orderMode === 'delivery' ? 'Express' : 'Mesa'}
          </span>
          <span data-id="element-6512" data-mp="3ck2m6m3i39ly">·</span>
          <span className={cn(step >= 2 ? 'text-brand-charcoal font-bold' : '')} data-id="element-6513" data-mp="2xtda7hi7tqh1">
            2 Productos
          </span>
          <span data-id="element-6514" data-mp="32qvjxa4249q3">·</span>
          <span className={cn(step >= 3 ? 'text-brand-charcoal font-bold' : '')} data-id="element-6515" data-mp="32q3pw3q3yk1x">
            3 Confirmar
          </span>
        </div>

        <button onClick={async () => {
        // Rancho San Francisco (SOLO mesero): "Salir" desde una mesa
        // NO debe cerrar sesión. El mesero trabaja toda su jornada
        // entrando y saliendo de mesas; cerrar sesión cada vez lo
        // obligaba a re-loguearse. Acá "Salir" lo devuelve EXACTAMENTE
        // a /admin/rancho-san-francisco/waiter (su pantalla principal,
        // la grilla de Mesas / step 1) MANTENIENDO la sesión activa —
        // a ningún otro lugar.
        //
        // ⚠️ Como YA estamos montados en /admin/<slug>/waiter, un
        // navigate() a la misma ruta no remonta el componente ni
        // resetea el estado en memoria — por eso el botón "no hacía
        // nada". Reseteamos el estado de la página a mano (mismo
        // patrón que el post-submit) para volver a la pantalla
        // principal, y luego garantizamos que la URL sea exactamente
        // /admin/rancho-san-francisco/waiter. Scope estricto: solo
        // Rancho + role='waiter'; ningún otro restaurante ni rol
        // cambia.
        if (isRanchoSanFrancisco && role === 'waiter') {
          setCart([]);
          setSelectedTable(null);
          setOrderMode('mesa');
          setCustomerInfo(EMPTY_CUSTOMER_INFO);
          setActiveCategoryId(null);
          setSearchQuery('');
          setStep(1);
          navigate('/admin/rancho-san-francisco/waiter');
          return;
        }
        // Meseros (role='waiter'): "Salir" cierra sesión de verdad
        // y los manda a la pantalla de login del restaurante. No
        // tienen permiso para ver el selector de estaciones ni
        // navegar a otras áreas del admin.
        if (role === 'waiter') {
          try {
            await signOut();
          } catch (e) {
            console.warn('[WaiterPOS] signOut error (non-blocking):', e);
          }
          // Hard redirect para garantizar que se limpie todo el
          // estado en memoria (carrito persistido, contexts, etc.).
          const slug = restaurant?.slug || 'rancho-san-francisco';
          window.location.href = `/admin/${slug}/login`;
          return;
        }
        // NatuCafé — el dueño quiere que "Salir" lo regrese directo
        // a la grilla de Mesas (su pantalla principal). El resto de
        // restaurantes mantienen el comportamiento original: vuelven
        // a /operacion para que el operador pueda cambiar de estación.
        if (isNatuCafe && restaurant?.slug) {
          navigate(`/admin/${restaurant.slug}/tables`);
        } else {
          navigate(`/admin/${restaurant.slug}/operacion`);
        }
      }} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors" title={role === 'waiter' && !isRanchoSanFrancisco ? 'Cerrar sesión' : 'Salir'} data-id="element-6516" data-mp="3v9j14efxn4lw">
          <LogOut className="w-4 h-4" data-id="element-6517" data-mp="12doczz2owdo7" />
          <span className="hidden sm:inline" data-id="element-6518" data-mp="17bpx8nbuqj19">
            {role === 'waiter' && !isRanchoSanFrancisco ? 'Cerrar sesión' : 'Salir'}
          </span>
        </button>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col overflow-hidden" data-id="element-6519" data-mp="xkv8ncb4v7cn">
        {step === 1 && <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8" data-id="element-6520" data-mp="jlbzqhahzrza">
            <div className="max-w-6xl mx-auto" data-id="element-6521" data-mp="nnqm3bddl9vb">
              {/* Mode selector (Mesa / Para llevar / Express) — Cabaña + NatuCafé */}
              {supportsMultiMode && <div className="mb-8" data-id="element-6522" data-mp="9qk3bgsnjb63">
                  <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3" data-id="element-6523" data-mp="emines6y670p">
                    Tipo de orden
                  </h2>
                  <div className="grid grid-cols-3 gap-2 max-w-2xl" data-id="element-6524" data-mp="2405kgcrhogb7">
                    {([{
                key: 'mesa',
                label: 'Mesa',
                emoji: '🍽️'
              }, {
                key: 'pickup',
                label: 'Para llevar',
                emoji: '🥡'
              }, {
                key: 'delivery',
                label: 'Express',
                emoji: '🛵'
              }] as {
                key: WaiterOrderMode;
                label: string;
                emoji: string;
              }[]).map(mode => <button key={mode.key} onClick={() => {
                setOrderMode(mode.key);
                if (mode.key !== 'mesa') {
                  setSelectedTable(null);
                }
              }} className={cn('flex flex-col items-center justify-center gap-1 py-4 px-3 rounded-2xl border-2 font-bold transition-all', orderMode === mode.key ? 'bg-brand-charcoal text-white border-brand-charcoal shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400')} data-id="element-6525" data-mp="2bkzso7tcek3x">
                        <span className="text-2xl leading-none" data-id="element-6526" data-mp="3dcqyoqmr9n9">
                          {mode.emoji}
                        </span>
                        <span className="text-sm" data-id="element-6527" data-mp="85e7imutzmj3">{mode.label}</span>
                      </button>)}
                  </div>
                </div>}

              {/* Customer info form (Para llevar / Express) — Cabaña + NatuCafé */}
              {supportsMultiMode && orderMode !== 'mesa' && <div className="mb-8 bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 max-w-2xl" data-id="element-6528" data-mp="18d25mcnqtmkf">
                  <h2 className="text-lg font-bold text-gray-900 mb-1" data-id="element-6529" data-mp="13dzapl1h8wq6">
                    {orderMode === 'delivery' ? 'Datos del cliente (Express)' : 'Datos del cliente (Para llevar)'}
                  </h2>
                  <p className="text-sm text-gray-500 mb-5" data-id="element-6530" data-mp="1cf6xb5ums94f">
                    {orderMode === 'delivery' ? 'Información que vas a anotar de la llamada para el repartidor.' : 'Información para identificar la orden cuando el cliente venga a retirarla.'}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-6531" data-mp="ygz3v7wszpy0">
                    <div className="sm:col-span-1" data-id="element-6532" data-mp="tne69gz4gxzc">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6533" data-mp="5ybo880kt34b">
                        Nombre del cliente *
                      </label>
                      <input type="text" value={customerInfo.name} onChange={e => setCustomerInfo(prev => ({
                  ...prev,
                  name: e.target.value
                }))} placeholder="Ej: María Pérez" className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-6534" data-mp="z8kcqwc12xx" />
                    </div>

                    <div className="sm:col-span-1" data-id="element-6535" data-mp="2mcf3sp25qfym">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6536" data-mp="2rb8fqd1spywo">
                        Teléfono {orderMode === 'delivery' && '*'}
                      </label>
                      <input type="tel" inputMode="tel" value={customerInfo.phone} onChange={e => setCustomerInfo(prev => ({
                  ...prev,
                  phone: e.target.value
                }))} placeholder="8888-8888" className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-6537" data-mp="1fwbg697zjrb2" />
                    </div>

                    {orderMode === 'delivery' && <>
                        <div className="sm:col-span-2" data-id="element-6538" data-mp="12fv0mwpbvh6l">
                          <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6539" data-mp="12ebit1o0wy2p">
                            Dirección de entrega *
                          </label>
                          <textarea value={customerInfo.address} onChange={e => setCustomerInfo(prev => ({
                    ...prev,
                    address: e.target.value
                  }))} placeholder="Ej: 200m sur de la iglesia, casa portón verde…" rows={2} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent resize-none" data-id="element-6540" data-mp="teer0r6c7j3f" />
                        </div>

                        <div className="sm:col-span-2" data-id="element-6541" data-mp="xktpgmwc64xg">
                          <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6542" data-mp="1bj0b94o0gegr">
                            Link de Google Maps (opcional)
                          </label>
                          <input type="url" inputMode="url" value={customerInfo.maps_url} onChange={e => setCustomerInfo(prev => ({
                    ...prev,
                    maps_url: e.target.value
                  }))} placeholder="https://maps.google.com/…" className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-6543" data-mp="46r8eufrjha6" />
                        </div>

                        {isNatuCafe ? <>
                            <div className="sm:col-span-1" data-id="element-6544" data-mp="2yow584rrjrnr">
                              <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6545" data-mp="32pfygx97ub4m">
                                Zona de entrega *
                              </label>
                              <select value={selectedMsZone} onChange={e => {
                      setSelectedMsZone(e.target.value);
                      setSelectedMsSubzone('');
                      setSelectedMsFee(0);
                      setSelectedMsCatalogId(null);
                    }} disabled={msZonesLoading} className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent disabled:opacity-50" data-id="element-6546" data-mp="2qdx3u1aji0jm">
                                <option value="" data-id="element-6547" data-mp="1tljrn9ln8hhv">Seleccionar zona...</option>
                                {msZoneNames.map(z => <option key={z} value={z} data-id="element-6548" data-mp="3ckznfrsnetdj">
                                    {z}
                                  </option>)}
                              </select>
                            </div>
                            <div className="sm:col-span-1" data-id="element-6549" data-mp="33mntbfskpdgc">
                              <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6550" data-mp="37ng6gk4kg2hw">
                                Localidad *
                              </label>
                              <select value={selectedMsSubzone} onChange={e => {
                      const sub = e.target.value;
                      setSelectedMsSubzone(sub);
                      const match = msSubzonesForZone.find(z => z.subzone === sub);
                      if (match) {
                        setSelectedMsFee(match.fee);
                        setSelectedMsCatalogId(match.id);
                      } else {
                        setSelectedMsFee(0);
                        setSelectedMsCatalogId(null);
                      }
                    }} disabled={!selectedMsZone || msZonesLoading} className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent disabled:opacity-50" data-id="element-6551" data-mp="45fy33v3lv3r">
                                <option value="" data-id="element-6552" data-mp="1tmbhgvvui4tw">
                                  Seleccionar localidad...
                                </option>
                                {msSubzonesForZone.map(z => <option key={`${z.id}-${z.subzone}`} value={z.subzone} data-id="element-6553" data-mp="10x4ixwwe5f8n">
                                    {z.subzone}
                                  </option>)}
                              </select>
                              {selectedMsSubzone && <p className="mt-1.5 text-xs font-bold text-green-600" data-id="element-6554" data-mp="3erah5dhvj7ip">
                                  Costo de envío:{' '}
                                  {formatCurrency(selectedMsFee)} — automático
                                </p>}
                            </div>
                          </> : <div className="sm:col-span-2" data-id="element-6555" data-mp="2w2dk51v3e0ws">
                            <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6556" data-mp="6c0jqioxiqh9">
                              Monto de express (opcional)
                            </label>
                            <div className="relative" data-id="element-6557" data-mp="bcwloqpq02k1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold pointer-events-none" data-id="element-6558" data-mp="ldyzkcyg7cgx">
                                ₡
                              </span>
                              <input type="number" inputMode="decimal" min="0" step="100" value={customerInfo.express_fee} onChange={e => setCustomerInfo(prev => ({
                      ...prev,
                      express_fee: e.target.value
                    }))} placeholder="Ej: 1500" className="w-full h-11 pl-8 pr-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-6559" data-mp="bnw4m77rnofy" />
                            </div>
                            <p className="mt-1.5 text-xs text-gray-500" data-id="element-6560" data-mp="1slqq9cct0whh">
                              Se sumará al total de la orden si se ingresa.
                            </p>
                          </div>}
                      </>}

                    <div className="sm:col-span-2" data-id="element-6561" data-mp="1ipnpoaxlp5wn">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6562" data-mp="1nnp6ep7k0t4y">
                        Notas (opcional)
                      </label>
                      <textarea value={customerInfo.notes} onChange={e => setCustomerInfo(prev => ({
                  ...prev,
                  notes: e.target.value
                }))} placeholder="Ej: tocar el timbre, sin cebolla, etc." rows={2} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent resize-none" data-id="element-6563" data-mp="1ely29osnwud7" />
                    </div>
                  </div>

                  <button onClick={() => {
              if (!customerInfo.name.trim()) return;
              if (orderMode === 'delivery' && (!customerInfo.address.trim() || !customerInfo.phone.trim())) return;
              if (isNatuCafe && orderMode === 'delivery' && (!selectedMsZone || !selectedMsSubzone)) return;
              setStep(2);
            }} disabled={!customerInfo.name.trim() || orderMode === 'delivery' && (!customerInfo.address.trim() || !customerInfo.phone.trim() || isNatuCafe && (!selectedMsZone || !selectedMsSubzone))} className={cn('mt-5 w-full h-12 rounded-xl font-bold text-base transition-all', customerInfo.name.trim() && (orderMode !== 'delivery' || customerInfo.address.trim() && customerInfo.phone.trim() && (!isNatuCafe || selectedMsZone && selectedMsSubzone)) ? 'bg-green-500 hover:bg-green-600 text-white shadow-sm' : 'bg-gray-100 text-gray-400 cursor-not-allowed')} data-id="element-6564" data-mp="5npyig6kkax9">
                    Continuar a productos →
                  </button>
                </div>}

              {/* Tables grid — only when in 'mesa' mode (or any non-Cabaña restaurant) */}
              {(!supportsMultiMode || orderMode === 'mesa') && <>
                  <h1 className="text-3xl font-black text-gray-900 mb-2" data-id="element-6565" data-mp="27n4naglquecq">
                    Selecciona una mesa
                  </h1>
                  <p className="text-gray-500 mb-8" data-id="element-6566" data-mp="2bpr6jt4cmqyb">
                    Toca una mesa para tomar la orden
                  </p>

                  {tablesLoading ? <div className="flex items-center justify-center py-20" data-id="element-6567" data-mp="2lmnhs6lmtr6k">
                      <div className="w-8 h-8 border-4 border-brand-charcoal border-t-transparent rounded-full animate-spin" data-id="element-6568" data-mp="3s2g3v7lhjew1"></div>
                    </div> : <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-id="element-6569" data-mp="37cluoaufnqvq">
                      {sortedTables.map(table => {
                const isOccupied = table.status !== 'free';
                return <button key={table.id} onClick={() => handleTableSelect(table)} className={cn('flex flex-col items-center justify-center p-6 rounded-2xl border-2 transition-all min-h-[140px]', isOccupied ? 'bg-amber-50 border-amber-200 hover:border-amber-400' : 'bg-white border-gray-100 hover:border-green-500 hover:shadow-md')} data-id="element-6570" data-mp="3h8p7qbqtp9xg">
                            <span className="text-5xl font-black text-gray-900 mb-2" data-id="element-6571" data-mp="25tjuidt2fszl">
                              {table.table_number}
                            </span>
                            {table.table_name && <span className="text-xs font-medium text-gray-500 mb-3 text-center line-clamp-1" data-id="element-6572" data-mp="l5zico11m21w">
                                {table.table_name}
                              </span>}
                            <div className={cn('px-3 py-1 rounded-full text-xs font-bold', isOccupied ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800')} data-id="element-6573" data-mp="bgfybf1lg25g">
                              {isOccupied ? 'Ocupada' : 'Libre'}
                            </div>
                          </button>;
              })}
                    </div>}
                </>}
            </div>
          </div>}

        {step === 2 && (selectedTable || supportsMultiMode && orderMode !== 'mesa') && <div className="flex-1 flex flex-col h-full overflow-hidden" data-id="element-6574" data-mp="3qc15k7gx63sc">
              {/* Sub-header */}
              <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0" data-id="element-6575" data-mp="3le9c390mm9m1">
                <div className="flex items-center gap-3" data-id="element-6576" data-mp="3gck27wp541zj">
                  <button onClick={() => setStep(1)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-6577" data-mp="hvy9kyz663ju">
                    <ArrowLeft className="w-6 h-6" data-id="element-6578" data-mp="7x8neu08pwxs" />
                  </button>
                  <div data-id="element-6579" data-mp="3w6o6d6bpkvt">
                    <h2 className="text-xl font-bold text-gray-900 leading-none" data-id="element-6580" data-mp="3wovq5yfrlj1">
                      {selectedTable ? `Mesa ${selectedTable.table_number}` : orderMode === 'delivery' ? `Express — ${customerInfo.name || 'Cliente'}` : `Para llevar — ${customerInfo.name || 'Cliente'}`}
                    </h2>
                    {selectedTable && selectedTable.table_name && <span className="text-sm text-gray-500" data-id="element-6581" data-mp="1d5b8jvo141gw">
                        {selectedTable.table_name}
                      </span>}
                    {!selectedTable && customerInfo.phone && <span className="text-sm text-gray-500" data-id="element-6582" data-mp="1my0iun7uf9an">
                        📞 {customerInfo.phone}
                      </span>}
                  </div>
                </div>

                <button onClick={() => setStep(3)} disabled={cart.length === 0} className={cn('flex items-center gap-3 px-4 py-2 rounded-xl font-bold transition-all', cart.length > 0 ? 'bg-brand-charcoal text-white hover:bg-gray-800 shadow-sm' : 'bg-gray-100 text-gray-400 cursor-not-allowed')} data-id="element-6583" data-mp="1vuhyo3r5jvcq">
                  <div className="flex items-center gap-2" data-id="element-6584" data-mp="14v1s5hpoc468">
                    <ShoppingBag className="w-5 h-5" data-id="element-6585" data-mp="1itrrl3zyufxr" />
                    <span className="bg-white/20 px-2 py-0.5 rounded-md text-sm" data-id="element-6586" data-mp="1ir5j92593weh">
                      {cartItemCount}
                    </span>
                  </div>
                  <span data-id="element-6587" data-mp="1sq4v0tlbc85p">{formatCurrency(orderTotal)}</span>
                </button>
              </div>

              {/* Search */}
              <div className="bg-white border-b border-gray-100 px-4 py-3 shrink-0" data-id="element-6588" data-mp="1nq8oaml8e2mg">
                <div className="relative max-w-2xl mx-auto" data-id="element-6589" data-mp="21iyd0ys59ln9">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-6590" data-mp="1xhwcmemhr3jp" />
                  <input type="text" inputMode="search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Buscar producto…" className="w-full h-12 pl-12 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-6591" data-mp="2bip56tt8fok6" />
                  {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-full" aria-label="Limpiar búsqueda" data-id="element-6592" data-mp="27eelkb2o7m57">
                      <X className="w-4 h-4" data-id="element-6593" data-mp="22ftmwg32haiv" />
                    </button>}
                </div>
              </div>

              {/* Categories — hide while searching */}
              {!searchQuery.trim() && <div className="bg-white border-b border-gray-100 overflow-x-auto shrink-0 hide-scrollbar" data-id="element-6594" data-mp="1siox86a0iwxb">
                  <div className="flex p-2 gap-2 min-w-max" data-id="element-6595" data-mp="23xaepdzb1al9">
                    {displayCategories.map(cat => {
              const isArmaTab = cat.id === ARMA_TU_PLATO_ID;
              return <button key={cat.id} onClick={() => setActiveCategoryId(cat.id)} className={cn('px-6 py-3 rounded-xl font-bold text-sm transition-colors whitespace-nowrap', activeCategoryId === cat.id ? isArmaTab ? 'bg-amber-500 text-white' : 'bg-brand-charcoal text-white' : isArmaTab ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-gray-50 text-gray-600 hover:bg-gray-100')} data-id="element-6596" data-mp="1tzm6pmcou6l5">
                          {cat.name}
                        </button>;
            })}
                  </div>
                </div>}

              {/* Items Grid */}
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50" data-id="element-6597" data-mp="3mu57scuwiiog">
                {armaTuPlatoEnabled && !searchQuery.trim() && activeCategoryId === ARMA_TU_PLATO_ID ? <ArmaTuPlatoPanel lines={armaLines} setLines={setArmaLines} onAddToCart={filledLines => {
            const newCartItems: WaiterCartItem[] = filledLines.map(text => {
              const syntheticId = `__custom__${crypto.randomUUID()}`;
              return {
                id: crypto.randomUUID(),
                menuItem: {
                  id: syntheticId,
                  name: text,
                  price: 0,
                  is_active: true,
                  is_customizable: false,
                  category_id: ARMA_TU_PLATO_ID,
                  restaurant_id: Number(restaurantId) || 0
                } as any,
                quantity: 1,
                options: [],
                notes: ''
              };
            });
            setCart(prev => [...prev, ...newCartItems]);
            setArmaLines(['', '', '', '', '', '']);
          }} data-id="element-6598" data-mp="2z6dq0tqlddfs" /> : (() => {
            const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const q = normalize(searchQuery.trim());
            const filteredItems = q ? activeItems.filter(item => {
              const haystack = normalize(`${item.name} ${item.description || ''}`);
              return haystack.includes(q);
            }) : activeItems.filter(item => item.category_id === activeCategoryId);
            if (filteredItems.length === 0) {
              return <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-6599" data-mp="1q7mr8orzdwcp">
                          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-6600" data-mp="1l9urx9p0b7yh">
                            <Search className="w-7 h-7 text-gray-400" data-id="element-6601" data-mp="2irbn07k2zvuz" />
                          </div>
                          <p className="text-lg font-bold text-gray-900 mb-1" data-id="element-6602" data-mp="2dwyyvk7ntwcw">
                            {q ? 'Sin resultados' : 'No hay productos'}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-6603" data-mp="g21wduicp8kj">
                            {q ? `No encontramos productos para "${searchQuery}"` : 'No hay productos en esta categoría'}
                          </p>
                        </div>;
            }
            const activeCat = categories.find(c => c.id === activeCategoryId);
            const isPizzaCat = activeCat?.name?.toLowerCase().includes('pizza');
            return <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-6604" data-mp="1dq2bwvwaw7ap">
                        {isEmilius && isPizzaCat && !q && <button key="arma-tu-pizza" onClick={() => setShowEmiliusArmaTuPizzaModal(true)} className="bg-[#111] rounded-2xl p-4 shadow-sm border border-[#6FA248]/30 flex flex-col h-full hover:border-[#6FA248] transition-all text-left group" data-id="element-6605" data-mp="1inc7ktaa9f6z">
                            <div className="flex-1" data-id="element-6606" data-mp="1wtnpitdipdpm">
                              <h3 className="font-bold text-white text-lg leading-tight mb-1" data-id="element-6607" data-mp="2bjlgfh2u0hii">
                                Armá tu propia pizza
                              </h3>
                              <p className="text-sm text-white/70 line-clamp-2 mb-3" data-id="element-6608" data-mp="2kkt0au4lpft2">
                                Elegí el tamaño y combiná dos especialidades
                                diferentes (mitad y mitad).
                              </p>
                            </div>
                            <div className="flex items-end justify-between mt-4" data-id="element-6609" data-mp="q8latwb1tr4o">
                              <span className="font-bold text-sm text-[#6FA248]" data-id="element-6610" data-mp="m79k1ngfuzuw">
                                Armar ahora
                              </span>
                              <div className="w-8 h-8 rounded-full bg-[#6FA248]/20 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-6611" data-mp="skdtp7s47amf">
                                <span className="text-lg" data-id="element-6612" data-mp="11m4txx3vyp8w">🍕</span>
                              </div>
                            </div>
                          </button>}
                        {filteredItems.map(item => {
                const cartItem = cart.find(c => c.menuItem.id === item.id);
                return <div key={item.id} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col h-full" data-id="element-6613" data-mp="ioark1x9jbzh">
                              <div className="flex-1" data-id="element-6614" data-mp="3maj6yfupfdd4">
                                <h3 className="font-bold text-gray-900 text-lg leading-tight mb-1 line-clamp-2" data-id="element-6615" data-mp="3etcw5v1f2g4e">
                                  {item.name}
                                </h3>
                                {item.description && <p className="text-sm text-gray-500 line-clamp-2 mb-3" data-id="element-6616" data-mp="34wqd9bsnhz50">
                                    {item.description}
                                  </p>}
                              </div>
                              <div className="flex items-end justify-between mt-4" data-id="element-6617" data-mp="2r2z1dwrg6364">
                                <span className="font-bold text-xl text-green-600" data-id="element-6618" data-mp="2m3w5p2xcx9at">
                                  {formatCurrency(Number(item.price))}
                                </span>

                                {isCabanaParrillera && selectedTable ? <button onClick={() => setSelectedProduct(item)} aria-label={`Agregar ${item.name} a la orden`} className="w-12 h-12 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-sm transition-colors" data-id="element-6619" data-mp="2d5ahar57f024">
                                    <Plus className="w-6 h-6" data-id="element-6620" data-mp="1tho4aq0w0h0k" />
                                  </button> : cartItem && !item.is_customizable &&
                    // Rancho San Francisco (id 28) — el cajero
                    // necesita poder agregar OTRA línea del mismo
                    // producto con notas distintas (ej. dos
                    // Gaseosas, una "fría sin hielo" y otra "con
                    // limón"). Si forzáramos el stepper -/qty/+
                    // como en los demás restaurantes, la segunda
                    // Gaseosa simplemente incrementa la primera
                    // y comparte sus notas — sin manera de
                    // diferenciarlas en la comanda.
                    //
                    // Fix: en Rancho, NUNCA usamos el stepper
                    // rápido. Cada click en "+" reabre el modal
                    // de detalle para que el cajero ponga notas/
                    // opciones nuevas si quiere. Si no pone
                    // notas, el modal seguirá deduplicando la
                    // línea (lógica existente de addToCart) —
                    // pero con notas distintas crea una línea
                    // nueva. Cero impacto fuera de Rancho.
                    !isRanchoSanFrancisco ? <div className="flex items-center bg-gray-100 rounded-full p-1" data-id="element-6621" data-mp="1q09v1xykspnp">
                                    <button onClick={e => {
                        e.stopPropagation();
                        updateQuantity(cartItem.id, -1);
                      }} className="w-8 h-8 flex items-center justify-center bg-white rounded-full text-gray-600 shadow-sm" data-id="element-6622" data-mp="2a2edg3fwkik8">
                                      <Minus className="w-4 h-4" data-id="element-6623" data-mp="2ss4bczeypkgd" />
                                    </button>
                                    <span className="w-8 text-center font-bold text-gray-900" data-id="element-6624" data-mp="2xtjrqe6732sf">
                                      {cartItem.quantity}
                                    </span>
                                    <button onClick={e => {
                        e.stopPropagation();
                        updateQuantity(cartItem.id, 1);
                      }} className="w-8 h-8 flex items-center justify-center bg-white rounded-full text-gray-600 shadow-sm" data-id="element-6625" data-mp="36q15zq9uvgse">
                                      <Plus className="w-4 h-4" data-id="element-6626" data-mp="v40gkt26v0jo" />
                                    </button>
                                  </div> : <button onClick={() => setSelectedProduct(item)} className="w-12 h-12 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-sm transition-colors" data-id="element-6627" data-mp="1ntz9x0nf1ju5">
                                    <Plus className="w-6 h-6" data-id="element-6628" data-mp="19swlwicv1zgl" />
                                  </button>}
                              </div>
                            </div>;
              })}
                      </div>;
          })()}
              </div>
            </div>}

        {step === 3 && (selectedTable || supportsMultiMode && orderMode !== 'mesa') && <div className="flex-1 flex flex-col h-full overflow-hidden bg-white" data-id="element-6629" data-mp="31lcouqhkw8x0">
              <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 shrink-0" data-id="element-6630" data-mp="2wtm7w1o4ymwm">
                <button onClick={() => setStep(2)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-6631" data-mp="3bhpjbqky6qst">
                  <ArrowLeft className="w-6 h-6" data-id="element-6632" data-mp="3ki5kgte7xlui" />
                </button>
                <h2 className="text-xl font-bold text-gray-900" data-id="element-6633" data-mp="3fk3z21tblafv">
                  {selectedTable ? `Confirmar Orden - Mesa ${selectedTable.table_number}` : orderMode === 'delivery' ? `Confirmar Express — ${customerInfo.name || 'Cliente'}` : `Confirmar Para Llevar — ${customerInfo.name || 'Cliente'}`}
                </h2>
              </div>

              <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8" data-id="element-6634" data-mp="5mvdike6vvag">
                <div className="max-w-3xl mx-auto" data-id="element-6635" data-mp="3o25y5c84jcld">
                  {submitError && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-red-800" data-id="element-6636" data-mp="3s2g6uwnawh3x">
                      <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-6637" data-mp="3e2f5lg34j7nx" />
                      <p className="text-sm font-medium" data-id="element-6638" data-mp="3j2jlp504e0dm">{submitError}</p>
                    </div>}

                  <div className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden mb-6" data-id="element-6639" data-mp="353tolpdaw0p9">
                    <div className="divide-y divide-gray-100" data-id="element-6640" data-mp="394vfnix2d6g5">
                      {cart.map(item => {
                  const optionsTotal = item.options.reduce((sum, opt) => sum + (Number(opt.price_delta) || 0), 0);
                  const itemTotal = (Number(item.menuItem.price) + optionsTotal) * item.quantity;
                  return <div key={item.id} className="p-4 flex gap-4" data-id="element-6641" data-mp="279sf9j61k3xx">
                            <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 h-fit shrink-0" data-id="element-6642" data-mp="22ao5aqbam26j">
                              <button onClick={() => updateQuantity(item.id, -1)} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-50 rounded-md" data-id="element-6643" data-mp="2yyvl33dktmco">
                                <Minus className="w-4 h-4" data-id="element-6644" data-mp="2l9jz40j8oeju" />
                              </button>
                              <span className="w-8 text-center font-bold text-gray-900" data-id="element-6645" data-mp="3ioeak0e5ewhr">
                                {item.quantity}
                              </span>
                              <button onClick={() => updateQuantity(item.id, 1)} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-50 rounded-md" data-id="element-6646" data-mp="3l8kh8fu5d120">
                                <Plus className="w-4 h-4" data-id="element-6647" data-mp="3v8us3tsplwe0" />
                              </button>
                            </div>

                            <div className="flex-1 min-w-0" data-id="element-6648" data-mp="82bw40dvwjna">
                              <div className="flex justify-between items-start gap-4" data-id="element-6649" data-mp="81si2urcufej">
                                <h4 className="font-bold text-gray-900 text-lg" data-id="element-6650" data-mp="czkikd62f9uc">
                                  {item.menuItem.name}
                                </h4>
                                <span className="font-bold text-gray-900 whitespace-nowrap" data-id="element-6651" data-mp="mpwzlveo8kvq">
                                  {formatCurrency(itemTotal)}
                                </span>
                              </div>

                              {item.options.length > 0 && <ul className="mt-1 space-y-0.5" data-id="element-6652" data-mp="i44guwl7nkts">
                                  {aggregateQuantityOptions(item.options).map((opt, idx) => <li key={idx} className="text-sm text-gray-500 flex justify-between" data-id="element-6653" data-mp="ruz6maxo12jz" data-mpi={idx}>
                                        <span data-id="element-6654" data-mp="3vby3jlm7glfv">
                                          • {opt.option_name}
                                          {opt.qty && opt.qty > 1 ? ` x ${opt.qty}` : ''}
                                        </span>
                                        {Number(opt.price_delta) > 0 && <span data-id="element-6655" data-mp="1khjyrdlytiol">
                                            +
                                            {formatCurrency(Number(opt.price_delta))}
                                          </span>}
                                      </li>)}
                                </ul>}

                              {item.notes && <p className="mt-2 text-sm text-amber-700 bg-amber-50 p-2 rounded-lg" data-id="element-6656" data-mp="3h6pg03fi57hw">
                                  Nota: {item.notes}
                                </p>}
                            </div>

                            <button onClick={() => removeItem(item.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg h-fit transition-colors" data-id="element-6657" data-mp="3cee5k6n4y3b3">
                              <X className="w-5 h-5" data-id="element-6658" data-mp="8rxe231eb0nb" />
                            </button>
                          </div>;
                })}
                    </div>

                    <div className="p-4 bg-white border-t border-gray-100 space-y-2" data-id="element-6659" data-mp="dl5gms07rilt">
                      {expressFeeAmount > 0 && <>
                          <div className="flex justify-between items-center text-base" data-id="element-6660" data-mp="3oaiia7urk82">
                            <span className="text-gray-500" data-id="element-6661" data-mp="3vteeq31xjgn8">Subtotal</span>
                            <span className="font-medium text-gray-700" data-id="element-6662" data-mp="3r1cthrmsafl2">
                              {formatCurrency(cartTotal)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-base" data-id="element-6663" data-mp="3h48gahax1uge">
                            <span className="text-gray-500" data-id="element-6664" data-mp="3c4dpedr554vc">
                              Monto de express
                            </span>
                            <span className="font-medium text-gray-700" data-id="element-6665" data-mp="384bqgccsipjr">
                              {formatCurrency(expressFeeAmount)}
                            </span>
                          </div>
                          <div className="border-t border-gray-100 pt-2" data-id="element-6666" data-mp="1rl057kelzw00" />
                        </>}
                      <div className="flex justify-between items-center" data-id="element-6667" data-mp="1wjlavzcmr7vu">
                        <span className="text-lg font-medium text-gray-500" data-id="element-6668" data-mp="21k8yeb1nh6lb">
                          Total a cobrar
                        </span>
                        <span className="text-3xl font-black text-gray-900" data-id="element-6669" data-mp="18vtuoknyyapa">
                          {formatCurrency(orderTotal)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button onClick={handleSubmitOrder} disabled={isSubmitting || cart.length === 0} className="w-full h-16 text-xl font-bold bg-green-500 hover:bg-green-600 text-white rounded-2xl" data-id="element-6670" data-mp="1imohpr91hebs">
                    {isSubmitting ? <div className="flex items-center gap-2" data-id="element-6671" data-mp="3fnxlza5gw7qi">
                        <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" data-id="element-6672" data-mp="3apcgowbcyxv7" />
                        <span data-id="element-6673" data-mp="3op5a4ze9rs6v">Enviando a cocina...</span>
                      </div> : <div className="flex items-center gap-2" data-id="element-6674" data-mp="3jnxygp90a8ux">
                        <Check className="w-6 h-6" data-id="element-6675" data-mp="2gcam8ky64di" />
                        <span data-id="element-6676" data-mp="2esuchzajtbq">Confirmar y Enviar Orden</span>
                      </div>}
                  </Button>
                </div>
              </div>
            </div>}
      </main>

      {/* Product Detail Modal */}
      {selectedProduct && <ProductDetailModal isOpen={!!selectedProduct} onClose={() => setSelectedProduct(null)} item={selectedProduct} onAdd={handleAddToCart} data-id="element-6677" data-mp="78nnec0sdknh" />}

      {showEmiliusArmaTuPizzaModal && <EmiliusArmaTuPizzaModal isOpen={showEmiliusArmaTuPizzaModal} onClose={() => setShowEmiliusArmaTuPizzaModal(false)} onAdd={handleAddToCart} pizzas={items.filter(i => {
      const cat = categories.find(c => c.id === i.category_id);
      return cat?.name?.toLowerCase().includes('pizza');
    })} data-id="element-6678" data-mp="zulq8cy5axgf" />}
    </div>;
}